import {useCallback, useMemo} from 'react';
import {useLocation} from 'react-router-dom';
import {getDocumentType, useIVStore} from '@chekin/shared';
import {useReservation} from 'context/reservation';
import {useVerificationSettings} from 'hooks/iv/useVerificationSettings';
import {useIVComputedDetails, usePath} from 'hooks';
import {PATHS} from 'routes/constants';

function useIVFlowSequence(): string[] {
  const documentType = useIVStore(getDocumentType);
  const {biomatch} = useIVComputedDetails();
  const {housing} = useReservation();
  const {isNeedVerification} = useVerificationSettings(housing?.id);

  return useMemo(() => {
    const twoSides = Boolean(documentType?.two_sides);

    return [
      PATHS.verification.main,
      ...(isNeedVerification ? [PATHS.verification.contacts] : []),
      PATHS.verification.documentSelection,
      PATHS.verification.front,
      ...(twoSides ? [PATHS.verification.back] : []),
      ...(biomatch ? [PATHS.verification.selfie] : []),
    ];
  }, [documentType?.two_sides, biomatch, isNeedVerification]);
}

export function useIVBackNavigation() {
  const {pathname} = useLocation();
  const {getFullPath, getMainPath} = usePath();
  const sequence = useIVFlowSequence();

  const getPreviousPath = useCallback(() => {
    const segment = pathname.split('/').filter(Boolean).pop() ?? '';
    const currentIndex = sequence.indexOf(segment);

    if (currentIndex > 0) {
      return getFullPath(sequence[currentIndex - 1]);
    }

    return getMainPath(PATHS.guestRegistration);
  }, [pathname, sequence, getFullPath, getMainPath]);

  return {getPreviousPath, sequence};
}
